import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseListResponse, parseResponse } from "lib/fetch";
import type { ThreadConversationInterface, ThreadInterface } from "types/thread";
import type { AdApplicationInterface } from "types/ad";
import {
    applyThreadOfferRequest,
    confirmThreadOfferRequest,
    getAllConversationsRequest,
    getArchivedConversationsRequest,
    getThreadOffersRequest,
    getThreadRequest,
    refuseThreadOfferRequest,
    showOrMaskConversationRequest,
} from "lib/api/thread";

export const fetchThread = (data: { serviceId: string; clientId: string }) =>
    parseResponse<ThreadInterface>(getThreadRequest(data.serviceId, data.clientId));

export const fetchAllConversations = () =>
    parseResponse<Array<ThreadConversationInterface>>(getAllConversationsRequest());

export const fetchArchivedConversations = () =>
    parseResponse<Array<ThreadConversationInterface>>(getArchivedConversationsRequest());

export const showOrMaskConversation = (messageId: string, data: HashMap<unknown>) =>
    parseResponse(showOrMaskConversationRequest(messageId, data));

export const archiveConversation = (serviceId: string, clientId: string, messageId?: string) =>
    showOrMaskConversation(messageId ?? serviceId, {
        show_or_mask: "mask",
        service_id: serviceId,
        client_id: clientId,
    });

export const threadKeys = {
    all: ["thread"] as const,
    conversations: () => [...threadKeys.all, "conversations"] as const,
    detail: (serviceId: string, clientId: string) =>
        [...threadKeys.all, "detail", serviceId, clientId] as const,
    offers: (serviceId: string, clientId: string) =>
        [...threadKeys.all, "offers", serviceId, clientId] as const,
};

export const fetchThreadOffers = (serviceId: string, clientId: string) =>
    parseListResponse<AdApplicationInterface>(getThreadOffersRequest(serviceId, clientId));

export const applyThreadOffer = (serviceId: string, clientId: string, data: HashMap<unknown>) =>
    parseResponse<AdApplicationInterface>(applyThreadOfferRequest(serviceId, clientId, data));

export const confirmThreadOffer = (serviceId: string, clientId: string, data: HashMap<unknown>) =>
    parseResponse<AdApplicationInterface>(confirmThreadOfferRequest(serviceId, clientId, data));

export const refuseThreadOffer = (serviceId: string, clientId: string, data: HashMap<unknown>) =>
    parseResponse<AdApplicationInterface>(refuseThreadOfferRequest(serviceId, clientId, data));

export const useThreadOffers = (serviceId: string, clientId: string, enabled = true) =>
    useQuery({
        queryKey: threadKeys.offers(serviceId, clientId),
        queryFn: () => fetchThreadOffers(serviceId, clientId),
        enabled: enabled && Boolean(serviceId) && Boolean(clientId),
    });

export const useApplyThreadOffer = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            serviceId,
            clientId,
            data,
        }: {
            serviceId: string;
            clientId: string;
            data: HashMap<unknown>;
        }) => applyThreadOffer(serviceId, clientId, data),
        onSuccess: (_response, { serviceId, clientId }) => {
            queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
            queryClient.invalidateQueries({ queryKey: threadKeys.detail(serviceId, clientId) });
        },
    });
};

export const useConfirmThreadOffer = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            serviceId,
            clientId,
            data,
        }: {
            serviceId: string;
            clientId: string;
            data: HashMap<unknown>;
        }) => confirmThreadOffer(serviceId, clientId, data),
        onSuccess: (_response, { serviceId, clientId }) => {
            queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
            queryClient.invalidateQueries({ queryKey: threadKeys.detail(serviceId, clientId) });
        },
    });
};

export const useRefuseThreadOffer = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            serviceId,
            clientId,
            data,
        }: {
            serviceId: string;
            clientId: string;
            data: HashMap<unknown>;
        }) => refuseThreadOffer(serviceId, clientId, data),
        onSuccess: (_response, { serviceId, clientId }) => {
            queryClient.invalidateQueries({ queryKey: threadKeys.offers(serviceId, clientId) });
            queryClient.invalidateQueries({ queryKey: threadKeys.detail(serviceId, clientId) });
        },
    });
};

export const useShowOrMaskConversation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: { messageId: string; data: HashMap<unknown> }) =>
            showOrMaskConversation(variables.messageId, variables.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: threadKeys.all });
        },
    });
};
